import useApiManager from 'networking/ApiManager';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { editIconBlack, upload, uploaded } from 'res/images';
import { dateFormat, timeFormat } from './Helper';
import Loader from './Loader';
import { Pagination } from 'whealth-core-web/components';
import CommonToolTip from './CommonToolTip';
import { useDispatch } from 'react-redux';
import { setReduxReportData } from 'redux/Slices';
import { pdf } from '@react-pdf/renderer';
import OPDReportPDF from './PrescriptionPdf/Content';
import PafPdfContent from './PrescriptionPdf/PafPdfContent';

function RenderReportTab(props) {
  const { userData, uploadReport } = props;
  const ApiClient = useApiManager();
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  
  useEffect(() => {
    getPatientReport();
  }, [id, currentPage]);

  const getPatientReport = () => {
    setIsLoading(true);
    let params = { page: currentPage };
    ApiClient.getPatientReports(id, false, params)
      .then((res) => {
        // Handle new API response structure
        const responseData = res.data;
        
        if (responseData && responseData.results) {
          // New API format with pagination
          setReportData(responseData.results);
          setTotalCount(responseData.count || 0);
          
          // Calculate total pages (assuming 10 items per page or based on results length)
          const itemsPerPage = responseData.results.length || 10;
          const calculatedPages = Math.ceil(responseData.count / itemsPerPage);
          setTotalPages(calculatedPages);
          
          dispatch(setReduxReportData(responseData.results));
        } else {
          // Fallback for old API format
          setTotalPages(res.headers['total-pages'] || 1);
          setReportData(res.data);
          dispatch(setReduxReportData(res.data));
        }
        
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching reports:', err);
        setIsLoading(false);
      });
  };

  // Download prescription PDF
  const downloadPrescriptionPdf = async (item) => {
    setIsLoading(true);
    try {
      const res = await ApiClient.prescriptionPdfDownload(id, item.id);
      let blob, blobURL;

      if (res.data) {
        blob = await pdf(<OPDReportPDF data={res.data} />).toBlob();
        blobURL = URL.createObjectURL(blob);
      } else {
        blob = new Blob([res.data], { type: 'application/pdf' });
        blobURL = URL.createObjectURL(blob);
      }
      
      setIsLoading(false);
      
      // Create iframe to print/download
      let iframe = document.createElement('iframe');
      document.body.appendChild(iframe);
      iframe.style.display = 'none';
      iframe.src = blobURL;
      iframe.onload = function () {
        setTimeout(function () {
          iframe.focus();
          iframe.contentWindow.print();
        }, 1);
      };
    } catch (err) {
      setIsLoading(false);
      console.error('Error downloading prescription PDF:', err);
    }
  };

  // Download PAF PDF
  const downloadPafPdf = async (item) => {
    setIsLoading(true);
    try {
      const res = await ApiClient.pafPdfDownload(item.id);
      let blob, blobURL;

      if (res.data) {
        blob = await pdf(<PafPdfContent data={res.data} />).toBlob();
        blobURL = URL.createObjectURL(blob);
      } else {
        blob = new Blob([res.data], { type: 'application/pdf' });
        blobURL = URL.createObjectURL(blob);
      }
      
      setIsLoading(false);
      
      // Create iframe to print/download
      let iframe = document.createElement('iframe');
      document.body.appendChild(iframe);
      iframe.style.display = 'none';
      iframe.src = blobURL;
      iframe.onload = function () {
        setTimeout(function () {
          iframe.focus();
          iframe.contentWindow.print();
        }, 1);
      };
    } catch (err) {
      setIsLoading(false);
      console.error('Error downloading PAF PDF:', err);
    }
  };
  const renderReportDetails = (item) => {
    // Determine the type of item and extract relevant data
    const dataType = item.data_type || 'report';
    const moduleName = item.module_name || 'Report';
    
    // Handle different data structures based on data_type
    let title = item.title || '';
    let description = item.description || '';
    let status = item.status;
    let channel = item.channel;
    let reportUrl = item.report || item.report_url;
    let adminName = item.admin?.full_name || '';
    let doctorName = item.doctor?.full_name || '';
    let updatedAt = item.updated_at || item.created_at;
    
    // Format status for display
    let statusDisplay = '';
    if (typeof status === 'string') {
      statusDisplay = status;
    } else if (typeof status === 'number') {
      // Handle numeric status for PAF
      const pafStatusMap = { 0: 'Created', 1: 'Linked To Prescription', 2: 'Prescribed' };
      statusDisplay = pafStatusMap[status] || status;
    }

    // Check if this is a prescription item
    const isPrescription = dataType === 'prescription';
    const isPrescriptionPublished = isPrescription && status === 'published';
    const isPaf = dataType === 'paf';
    
    return (
      <div className='cardDataReportBorder'>
        <div className='cardMilestone w-100'>
          <div className='d-flex'>
            <div className='newMilestone info w-100 reportTab'>
              <div className='p-2 mt-3 mileStone rounded'>
                <div className='d-flex justify-content-between'>
                  <div className='cardHeading'>
                    <div className='ms-auto semiBold'>
                      <div className='capitalize'>
                        <div className='d-flex justify-content-between align-items-center'>
                          {title && <strong className='pointer'>{title}</strong>}
                          {moduleName && !title && <strong className='pointer'>{moduleName}</strong>}
                        </div>
                      </div>
                    </div>
                    <div className='d-flex'>
                      {statusDisplay && (
                        <div className='mt-2 badge badge-light capitalize'>{statusDisplay}</div>
                      )}
                      {channel && <div className='ms-2 mt-2 badge badge-light capitalize'>{channel}</div>}
                    </div>
                    <div className='d-flex mt-2'>
                      <div className='d-flex w-100'>
                        <span className='prescriptiontime capitalize'>{description}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className='d-flex'>
                      {/* For Prescription: Show only download icon if published */}
                      {isPrescription ? (
                        <>
                            <div className='ms-auto d-flex'>
                              <div className='mx-2' style={{ cursor: 'pointer' }}>
                                <CommonToolTip
                                  placement={'top'}
                                  item={
                                    <span 
                                      className='material-icons-outlined' 
                                      onClick={() => downloadPrescriptionPdf(item)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      visibility
                                    </span>
                                  }
                                  showValue={'Download PDF'}
                                />
                              </div>
                            </div>
                        </>
                      ) : isPaf ? (
                        <>
                          {/* For PAF: Show download icon for PDF */}
                          <div className='ms-auto d-flex'>
                            <div className='mx-2' style={{ cursor: 'pointer' }}>
                              <CommonToolTip
                                placement={'top'}
                                item={
                                  <span 
                                    className='material-icons-outlined' 
                                    onClick={() => downloadPafPdf(item)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    download
                                  </span>
                                }
                                showValue={'Download PAF PDF'}
                              />
                            </div>
                            <div className='mx-2' style={{ cursor: 'pointer' }}>
                              <CommonToolTip
                                placement={'top'}
                                item={
                                  <span 
                                    className='material-icons-outlined' 
                                    onClick={() => window.open(`/patient/${id}/paf/${item.id}`, '_blank')}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    visibility
                                  </span>
                                }
                                showValue={'View PAF'}
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* For Report: Show download and edit icons */}
                          <div className='ms-auto d-flex'>
                            <div className='mx-2' style={{ cursor: 'pointer' }}>
                              {reportUrl && (
                                <CommonToolTip
                                  placement={'top'}
                                  item={
                                    <a href={reportUrl} target='_blank' rel='noopener noreferrer' style={{ cursor: 'pointer' }}>
                                      <img src={uploaded} className='icon' alt='Download' style={{ cursor: 'pointer' }} />
                                    </a>
                                  }
                                  src={editIconBlack}
                                  className='icon'
                                  style={{ cursor: 'pointer' }}
                                  showValue={'Download Report'}
                                />
                              )}
                            </div>
                          </div>
                          <div className='d-flex'>
                            <div className='d-flex ms-auto'>
                              <div className='goalAchivement' style={{ cursor: 'pointer' }}>
                                <CommonToolTip
                                  placement={'top'}
                                  item={
                                    <img
                                      onClick={() =>
                                        navigate(`/patient/${id}/reports/${item.id}/edit`, {
                                          state: {
                                            item,
                                          },
                                        })
                                      }
                                      src={editIconBlack}
                                      className='icon'
                                      style={{ cursor: 'pointer' }}
                                      alt='Edit'
                                    />
                                  }
                                  showValue={'Edit'}
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className='mt-2 justify-content-end date font-14px text-end'>
                      {adminName || doctorName}
                      <div className='date text-nowrap'>
                        {updatedAt && dateFormat(updatedAt) + ' · ' + timeFormat(updatedAt)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className='d-flex justify-content-between align-items-center'>
        <div className='pt-3'>
          {!userData?.read_only && !['ee', 'ee_manager'].includes(userData?.role) && (
            <button onClick={uploadReport} type='button' className='btn mt-3 btn-outline-primary'>
              <img src={upload} className='icon' alt='Upload' />
              Upload Report
            </button>
          )}
        </div>
        {totalCount > 0 && (
          <div className='pt-3 text-muted'>
            Showing {reportData.length} of {totalCount} reports
          </div>
        )}
      </div>
      
      {isLoading ? (
        <Loader show={isLoading} />
      ) : reportData && reportData.length > 0 ? (
        <>
          {reportData.map((item, index) => {
            return <div key={item.id || index}>{renderReportDetails(item)}</div>;
          })}
          <div className='d-flex justify-content-end'>
            <div className='patientPagination mt-3'>
              <Pagination totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
            </div>
          </div>
        </>
      ) : (
        <div className='d-flex justify-content-center align-items-center mt-5'>
          <div className='text-muted'>No reports found</div>
        </div>
      )}
    </>
  );
}

export default RenderReportTab;
